import { sanitizeRedirectUrl } from '@auth/features/auth/lib/redirect-url';
import {
  createRoute,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { protectedRoute, rootRoute } from './_root';

// `__clerk_ticket` must be declared on BOTH auth routes: TanStack drops any
// search key a route's `validateSearch` doesn't return, which would silently
// discard a Clerk application invitation the moment the router took over.
// Both keys are declared OPTIONAL so existing `navigate({ to: '/sign-in' })`
// call sites (root guard, preview redirect, goSignIn) stay valid.
const clerkTicket = (
  input: Record<string, unknown>
): { __clerk_ticket?: string } =>
  typeof input.__clerk_ticket === 'string'
    ? { __clerk_ticket: input.__clerk_ticket }
    : {};

export const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'sign-in',
  validateSearch: (
    input: Record<string, unknown>
  ): { redirect_url?: string; __clerk_ticket?: string } => ({
    redirect_url: sanitizeRedirectUrl(input.redirect_url),
    ...clerkTicket(input),
  }),
  component: lazyRouteComponent(() =>
    import('@auth/pages/sign-in').then((m) => ({ default: m.SignInPage }))
  ),
});

export const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'sign-up',
  validateSearch: (
    input: Record<string, unknown>
  ): { __clerk_ticket?: string } => clerkTicket(input),
  component: lazyRouteComponent(() =>
    import('@auth/pages/sign-up').then((m) => ({ default: m.SignUpPage }))
  ),
});

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'forgot-password',
  component: lazyRouteComponent(() =>
    import('@auth/pages/forgot-password').then((m) => ({
      default: m.ForgotPasswordPage,
    }))
  ),
});

export const embedWidgetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'embed-widget',
  validateSearch: (input: Record<string, unknown>) => ({
    organization:
      typeof input.organization === 'string' ? input.organization : undefined,
  }),
  component: lazyRouteComponent(() =>
    import('@auth/pages/embed-widget').then((m) => ({
      default: m.EmbedWidgetPage,
    }))
  ),
});

// The profile route is now a tabbed shell (Personal / Security / Organization).
// It renders `ProfileLayout` (TabBar + <Outlet/>); each tab is a child route.
// `profileRoute` itself has no leaf content — the index child redirects to the
// Personal tab so `/profile` always lands somewhere concrete.
export const profileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'profile',
  component: lazyRouteComponent(() =>
    import('@auth/pages/profile').then((m) => ({ default: m.ProfileLayout }))
  ),
});

export const profileIndexRoute = createRoute({
  getParentRoute: () => profileRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/profile/personal' });
  },
});

export const profilePersonalRoute = createRoute({
  getParentRoute: () => profileRoute,
  path: 'personal',
  component: lazyRouteComponent(() =>
    import('@auth/pages/profile').then((m) => ({
      default: m.PersonalTabPage,
    }))
  ),
});

export const profileSecurityRoute = createRoute({
  getParentRoute: () => profileRoute,
  path: 'security',
  component: lazyRouteComponent(() =>
    import('@auth/pages/profile').then((m) => ({
      default: m.SecurityTabPage,
    }))
  ),
});

export const profileOrganizationRoute = createRoute({
  getParentRoute: () => profileRoute,
  path: 'organization',
  component: lazyRouteComponent(() =>
    import('@auth/pages/profile').then((m) => ({
      default: m.OrganizationTabPage,
    }))
  ),
});
